import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { StudioMeasureFieldConfig } from './studio-measure-input.config';

type MeasureInputMode = 'numeric' | 'keyword' | 'custom';

interface ParsedMeasureValue {
  mode: MeasureInputMode;
  unit: string;
  inputText: string;
}

interface MeasurePresetSection {
  id: string;
  label: string;
  values: string[];
}

interface MeasureTypeOption {
  value: string;
  label: string;
  kind: 'unit' | 'special';
}

interface MeasureTypeSection {
  id: string;
  label: string;
  options: MeasureTypeOption[];
}

@Component({
  selector: 'app-studio-measure-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './studio-measure-input.component.html',
  styleUrl: './studio-measure-input.component.scss'
})
export class StudioMeasureInputComponent implements OnChanges {
  @Input() value = '';
  @Input({ required: true }) config!: StudioMeasureFieldConfig;
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('rootElement') private rootElementRef?: ElementRef<HTMLElement>;
  @ViewChild('valueInput') private valueInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('presetMenu') private presetMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('unitButton') private unitButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('unitMenu') private unitMenuRef?: ElementRef<HTMLElement>;

  draftInputText = '';
  selectedUnit = '';
  showPresetMenu = false;
  showUnitMenu = false;
  currentMode: MeasureInputMode = 'numeric';
  private lastNumericInputText = '';
  private lastNumericUnit = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['config']) {
      this.syncFromValue();
    }
  }

  handleInput(value: string): void {
    if (this.currentMode === 'keyword') {
      return;
    }

    this.draftInputText = value;
    this.showPresetMenu = true;
    this.showUnitMenu = false;
    this.emitIfImmediatelyResolvable();
  }

  openPresetMenu(): void {
    if (this.currentMode === 'keyword') {
      return;
    }

    this.showPresetMenu = true;
    this.showUnitMenu = false;
  }

  handleFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget as HTMLElement | null;
    if (!nextTarget) {
      return;
    }

    if (this.valueInputRef?.nativeElement === nextTarget || this.presetMenuRef?.nativeElement.contains(nextTarget)) {
      return;
    }

    if (this.unitButtonRef?.nativeElement.contains(nextTarget) || this.unitMenuRef?.nativeElement.contains(nextTarget)) {
      this.showPresetMenu = false;
      return;
    }

    this.closeMenus();
    this.commitDraftValue();
  }

  toggleUnitMenu(): void {
    this.showUnitMenu = !this.showUnitMenu;
    this.showPresetMenu = false;
  }

  selectTypeOption(option: MeasureTypeOption): void {
    if (option.kind === 'special') {
      this.currentMode = 'keyword';
      this.draftInputText = option.value;
      this.showUnitMenu = false;
      this.showPresetMenu = false;
      this.valueChange.emit(option.value);
      return;
    }

    this.changeUnit(option.value);
  }

  changeUnit(unit: string): void {
    this.selectedUnit = unit;
    this.currentMode = 'numeric';
    this.showUnitMenu = false;
    const sourceText = this.draftInputText.trim()
      ? this.draftInputText
      : (this.lastNumericInputText || '');
    const normalized = this.normalizeNumericText(sourceText);
    if (normalized) {
      this.draftInputText = normalized;
      this.rememberNumericState(normalized, unit);
      this.valueChange.emit(this.composeNumericValue(normalized, unit));
      return;
    }

    const fallbackPreset = this.presetsForUnit(unit)[0] ?? '0';
    this.draftInputText = fallbackPreset;
    this.rememberNumericState(fallbackPreset, unit);
    this.valueChange.emit(this.composeNumericValue(fallbackPreset, unit));
  }

  selectPreset(preset: string): void {
    this.currentMode = 'numeric';
    this.draftInputText = preset;
    this.rememberNumericState(preset, this.selectedNumericUnit);
    this.valueChange.emit(this.composeNumericValue(preset, this.selectedNumericUnit));
    this.closeMenus();
  }

  nudgeValue(direction: 1 | -1): void {
    const normalized = this.normalizeNumericText(this.draftInputText);
    if (!normalized) {
      return;
    }

    const step = this.resolveStep(this.selectedNumericUnit);
    const nextText = normalized
      .split(/\s+/)
      .filter(Boolean)
      .map((token) => {
        const nextNumber = Number.parseFloat(token) + (direction * step);
        const clamped = this.config.allowNegative ? nextNumber : Math.max(0, nextNumber);
        return this.formatNumber(clamped);
      })
      .join(' ');

    this.draftInputText = nextText;
    this.rememberNumericState(nextText, this.selectedNumericUnit);
    this.valueChange.emit(this.composeNumericValue(nextText, this.selectedNumericUnit));
    this.closeMenus();
  }

  get currentUnitOptions() {
    return this.config.unitOptions;
  }

  get presetSections(): MeasurePresetSection[] {
    const sections: MeasurePresetSection[] = [];

    const numericPresets = this.presetsForUnit(this.selectedNumericUnit);
    if (numericPresets.length) {
      sections.push({
        id: 'common',
        label: 'Common',
        values: numericPresets
      });
    }

    if (!sections.length && this.config.fallbackPresets?.length) {
      sections.push({
        id: 'fallback',
        label: 'Common',
        values: this.config.fallbackPresets
      });
    }

    return sections;
  }

  get showPresetSectionHeadings(): boolean {
    return this.presetSections.length > 1;
  }

  get specialValueOptions(): string[] {
    return this.config.specialValueOptions ?? [];
  }

  get typeSections(): MeasureTypeSection[] {
    const sections: MeasureTypeSection[] = [];

    if (this.specialValueOptions.length) {
      sections.push({
        id: 'special',
        label: 'Modes',
        options: this.specialValueOptions.map((value) => ({
          value,
          label: value,
          kind: 'special' as const
        }))
      });
    }

    sections.push({
      id: 'units',
      label: 'Units',
      options: this.currentUnitOptions.map((option) => ({
        value: option.value,
        label: option.label,
        kind: 'unit' as const
      }))
    });

    return sections;
  }

  get showTypeSectionHeadings(): boolean {
    return this.typeSections.length > 1;
  }

  get selectedNumericUnit(): string {
    if (this.selectedUnit) {
      return this.selectedUnit;
    }

    return this.config.defaultUnit;
  }

  get stepperDisabled(): boolean {
    return this.currentMode !== 'numeric' || !this.normalizeNumericText(this.draftInputText);
  }

  get selectedUnitLabel(): string {
    if (this.currentMode === 'keyword' && this.specialValueOptions.includes(this.draftInputText.trim())) {
      return this.draftInputText.trim();
    }

    return this.currentUnitOptions.find((option) => option.value === this.selectedNumericUnit)?.label ?? this.selectedNumericUnit;
  }

  isPresetActive(preset: string): boolean {
    return this.currentMode === 'numeric' && this.normalizeNumericText(this.draftInputText) === preset;
  }

  isTypeOptionActive(option: MeasureTypeOption): boolean {
    if (option.kind === 'special') {
      return this.currentMode === 'keyword' && this.draftInputText.trim() === option.value;
    }

    return this.currentMode === 'numeric' && this.selectedNumericUnit === option.value;
  }

  get displayInputValue(): string {
    return this.currentMode === 'keyword' ? '' : this.draftInputText;
  }

  get inputPlaceholder(): string {
    if (this.currentMode === 'keyword') {
      return this.draftInputText.trim();
    }

    return '';
  }

  get inputReadOnly(): boolean {
    return this.currentMode === 'keyword';
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as Node | null;
    const rootElement = this.rootElementRef?.nativeElement;
    if (!target || !rootElement || rootElement.contains(target)) {
      return;
    }

    this.closeMenus();
    this.commitDraftValue();
  }

  private syncFromValue(): void {
    if (!this.config) {
      return;
    }

    const parsedValue = this.parseValue(this.value);
    this.currentMode = parsedValue.mode;
    this.selectedUnit = parsedValue.unit;
    this.draftInputText = parsedValue.inputText;
    if (parsedValue.mode === 'numeric' && parsedValue.inputText) {
      this.rememberNumericState(parsedValue.inputText, parsedValue.unit);
    }
  }

  private closeMenus(): void {
    this.showPresetMenu = false;
    this.showUnitMenu = false;
  }

  private emitIfImmediatelyResolvable(): void {
    if (this.currentMode === 'keyword') {
      this.valueChange.emit(this.draftInputText.trim());
      return;
    }

    if (!this.draftInputText.trim()) {
      this.valueChange.emit('');
      return;
    }

    const normalized = this.normalizeNumericText(this.draftInputText);
    if (!normalized) {
      return;
    }

    this.currentMode = 'numeric';
    this.rememberNumericState(normalized, this.selectedNumericUnit);
    this.valueChange.emit(this.composeNumericValue(normalized, this.selectedNumericUnit));
  }

  private commitDraftValue(): void {
    if (this.currentMode === 'keyword') {
      this.valueChange.emit(this.draftInputText.trim());
      return;
    }

    if (!this.draftInputText.trim()) {
      this.valueChange.emit('');
      return;
    }

    const normalized = this.normalizeNumericText(this.draftInputText);
    if (normalized) {
      this.currentMode = 'numeric';
      this.draftInputText = normalized;
      this.rememberNumericState(normalized, this.selectedNumericUnit);
      this.valueChange.emit(this.composeNumericValue(normalized, this.selectedNumericUnit));
      return;
    }

    this.currentMode = 'custom';
    this.valueChange.emit(this.draftInputText.trim());
  }

  private parseValue(rawValue: string | null | undefined): ParsedMeasureValue {
    const trimmedValue = (rawValue ?? '').trim();
    if (!trimmedValue) {
      return {
        mode: 'numeric',
        unit: this.config.defaultUnit,
        inputText: ''
      };
    }

    if (this.specialValueOptions.includes(trimmedValue)) {
      return {
        mode: 'keyword',
        unit: this.config.defaultUnit,
        inputText: trimmedValue
      };
    }

    const tokens = trimmedValue.split(/\s+/).filter(Boolean);
    const parsedTokens = tokens.map((token) => token.match(/^(-?\d*\.?\d+)([a-z%]*)$/i));
    if (parsedTokens.every(Boolean)) {
      const units = parsedTokens
        .map((match) => match?.[2] ?? '')
        .filter((unit) => unit.length > 0);
      const distinctUnits = Array.from(new Set(units));
      if (distinctUnits.length <= 1) {
        const numericText = parsedTokens.map((match) => match?.[1] ?? '').join(' ');
        const resolvedUnit = distinctUnits[0]
          ?? (this.config.allowUnitless ? '' : this.config.defaultUnit);

        return {
          mode: 'numeric',
          unit: resolvedUnit,
          inputText: numericText
        };
      }
    }

    return {
      mode: 'custom',
      unit: this.config.defaultUnit,
      inputText: trimmedValue
    };
  }

  private normalizeNumericText(value: string): string | null {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    const tokens = trimmedValue.split(/\s+/).filter(Boolean);
    if (!tokens.length) {
      return '';
    }

    const normalizedTokens: string[] = [];
    for (const token of tokens) {
      const parsedValue = Number.parseFloat(token);
      if (!Number.isFinite(parsedValue)) {
        return null;
      }

      const clampedValue = this.config.allowNegative ? parsedValue : Math.max(0, parsedValue);
      normalizedTokens.push(this.formatNumber(clampedValue));
    }

    return normalizedTokens.join(' ');
  }

  private composeNumericValue(numericText: string, unit: string): string {
    const normalizedText = this.normalizeNumericText(numericText);
    if (normalizedText === null) {
      return numericText.trim();
    }

    if (!normalizedText) {
      return '';
    }

    return normalizedText
      .split(/\s+/)
      .filter(Boolean)
      .map((token) => (unit ? `${token}${unit}` : token))
      .join(' ');
  }

  private presetsForUnit(unit: string): string[] {
    const presetMap = this.config.valuePresetsByUnit ?? {};
    return presetMap[unit] ?? this.config.fallbackPresets ?? [];
  }

  private resolveStep(unit: string): number {
    switch (unit) {
      case 'rem':
      case 'em':
        return 0.25;
      case 'px':
      case '%':
      case 'vw':
      case 'vh':
      case 'vmin':
      case 'vmax':
      case 'dvw':
      case 'dvh':
      case 'svw':
      case 'svh':
      case 'lvw':
      case 'lvh':
      case 'ch':
        return 1;
      case '':
        return 0.1;
      default:
        return 1;
    }
  }

  private formatNumber(value: number): string {
    const rounded = Math.round(value * 1000) / 1000;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/\.?0+$/, '');
  }

  private rememberNumericState(inputText: string, unit: string): void {
    this.lastNumericInputText = inputText;
    this.lastNumericUnit = unit;
  }
}
